import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { WorkspaceSelectList } from '@nx-frontend/features/workspace-select';
import { EntityMenuAction } from '@nx-frontend/shared/ui/entity-menu';
import { FloatingPopup } from '@nx-frontend/shared/ui/floating-popup';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useChangeWorkspace, useNavigateWithCheckUnsavedChanges, useOpenInvitePopup } from '@nx-frontend/shared/hooks';
import { useCustomizerCheckChanges } from '@nx-frontend/shared/hooks/use-customizer-check-changes';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $opCanManageBilling,
  $opCanManageCustomHTMLIntegrations,
  $opCanManageWorkspaces,
  $opCanManageWorkspacesBranding,
  $orgRoleState,
} from '@nx-frontend/shared/store/org-role';
import { $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import { OrganizationPrivilege, PopupType } from '@nx-frontend/shared/types';
import { useOpenBranding } from './hooks/open-branding';
import { useOpenCustomCode } from './hooks/open-custom-code';

export function SidebarWorkspaceSelect() {
  const [
    currentWorkspace,
    opCanManageWorkspaces,
    opCanManageWorkspacesBranding,
    opCanManageCustomHTMLIntegrations,
    wpCanManageMembers,
    opCanManageBilling,
    orgRoleState,
  ] = useUnit([
    $currentWorkspace,
    $opCanManageWorkspaces,
    $opCanManageWorkspacesBranding,
    $opCanManageCustomHTMLIntegrations,
    $wpCanManageMembers,
    $opCanManageBilling,
    $orgRoleState,
  ]);
  const t = useTranslations('sidebar');

  const router = useRouter();
  const openInvitePopup = useOpenInvitePopup();
  const openBranding = useOpenBranding();
  const openCustomCode = useOpenCustomCode();
  const changeWorkspace = useChangeWorkspace();
  const navigateWithCheckUnsavedChanges = useNavigateWithCheckUnsavedChanges();
  const customizerCheckChanges = useCustomizerCheckChanges();

  const [showDropdown, setShowDropdown] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  const handleOnClose = () => {
    setShowDropdown(false);
  };

  const workspaceClick = () => {
    setShowDropdown(true);
  };

  const actions = useMemo(() => {
    const items: EntityMenuAction[] = [];
    if (opCanManageWorkspaces) {
      items.push({
        icon: 'COG',
        name: t('sidebar-manage'),
        action: () => {
          setShowDropdown(false);
          customizerCheckChanges(() => {
            router.push(`/space/settings/org/${currentWorkspace.orgId}/ws/${currentWorkspace.globalId}`);
          });
        },
      });
    }
    if (wpCanManageMembers) {
      items.push({
        icon: 'USER_PLUS',
        name: orgRoleState.privileges.includes(OrganizationPrivilege.CAN_ADD_MEMBERS)
          ? t('sidebar-invite_members_and_clients')
          : t('sidebar-invite_members'),
        action: () => {
          setShowDropdown(false);
          customizerCheckChanges(() => {
            openInvitePopup({
              payloadType: PopupType.INVITE,
              subjectType: 'workspace',
            });
          });
        },
      });
    }
    if (opCanManageWorkspacesBranding || opCanManageBilling) {
      items.push({
        icon: 'STAR',
        name: t('sidebar-branding_public_pages'),
        action: () => {
          setShowDropdown(false);
          customizerCheckChanges(() => {
            openBranding();
          });
        },
      });
    }
    if (opCanManageCustomHTMLIntegrations || opCanManageBilling) {
      items.push({
        icon: 'CODE_INLINE',
        name: t('sidebar-integrations'),
        action: () => {
          setShowDropdown(false);
          customizerCheckChanges(() => {
            openCustomCode();
          });
        },
      });
    }
    return items;
  }, [
    currentWorkspace.globalId,
    currentWorkspace.orgId,
    opCanManageBilling,
    opCanManageCustomHTMLIntegrations,
    opCanManageWorkspaces,
    opCanManageWorkspacesBranding,
    openBranding,
    openCustomCode,
    openInvitePopup,
    orgRoleState.privileges,
    router,
    wpCanManageMembers,
    t,
    customizerCheckChanges,
  ]);

  if (!currentWorkspace) {
    return;
  }

  const handleWorkspaceSelect = (id: string) => {
    if (id === currentWorkspace.globalId) {
      return;
    }
    setShowDropdown(false);
    customizerCheckChanges(() => {
      changeWorkspace(id);
      navigateWithCheckUnsavedChanges(`/space/${id}/all`);
    });
  };

  return (
    <div>
      <div className={classNames('flex items-center rounded-md cursor-pointer w-full')} onClick={workspaceClick}>
        <span className="text-lg font-semibold whitespace-nowrap min-w-0 text-ellipsis overflow-hidden">
          {currentWorkspace.title}
        </span>
        <span className="ml-auto">
          <Icon icon={IconsMap.ARROW_DOWN} className="text-neutral-500" />
        </span>
      </div>
      <div ref={targetRef} className="relative -bottom-3"></div>
      <FloatingPopup
        show={showDropdown}
        targetRef={targetRef}
        closeButton={false}
        position={'sidebar_left'}
        onClose={handleOnClose}
        className={['!p-0', '!rounded', '!min-w-60', 'mt-1', '!left-4']}
        containerClassName={['!p-0']}
      >
        <WorkspaceSelectList
          workspaceId={currentWorkspace.globalId}
          actions={actions}
          select={handleWorkspaceSelect}
          onOpenCreatePopup={() => setShowDropdown(false)}
        />
      </FloatingPopup>
    </div>
  );
}
